import * as _ from "lodash";
import { Datum } from "@ant-design/charts";

import {
  ChartEntity,
  DataDescriptorEntity,
  DataPointsMeta,
  EventSourceType,
  ExtendedEventEntity,
  Line,
  LineData
} from "@ctra/api";
import { getTimeOfDay, Optional } from "@ctra/utils";

import { ChartContextType } from "../../../../providers";
import { SeriesType } from "../../../../typings";
import { filterLineBySeries } from "../../../../utils";

/**
 * Line chart config processor
 */
export class LineChartProcessor {
  public identity = "LineChartProcessor";

  /**
   * Check if the legend is visible
   * @param {ChartContextType<LineData>["values"]["meta"]} meta
   * @param {ChartContextType<LineData>["values"]["series"]} series
   * @param variants
   * @returns {boolean}
   */
  public isLegendVisible(
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"],
    variants: unknown
  ) {
    return true;
  }

  /**
   * Filter unwanted items from the line data (the info which does not belong to any series)
   * @returns {Record<ChartEntity["id"], Optional<Line>>}
   */
  getFilteredRawData(
    data: ChartContextType<LineData>["values"]["data"],
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"]
  ): Record<ChartEntity["id"], Optional<Line>> {
    return _.mapValues(data, (pointCollection, key) => {
      if (!!pointCollection) {
        const pointCollectionMeta: Optional<DataPointsMeta> = _.get(meta, [key]);

        if (!!pointCollectionMeta) {
          return filterLineBySeries(pointCollection, pointCollectionMeta, series);
        }

        return pointCollection;
      }

      return pointCollection;
    });
  }

  /**
   * Same as filtered data
   * @param {ChartContextType<LineData>["data"]} data
   * @param {ChartContextType<LineData>["meta"]} meta
   * @param {ChartContextType<LineData>["series"]} series
   * @returns {ChartContextType<LineData>["data"]}
   */
  getConsolidatedData(
    data: ChartContextType<LineData>["values"]["data"],
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"]
  ): ChartContextType<LineData>["values"]["data"] {
    return this.getFilteredRawData(data, meta, series);
  }

  /**
   * Remove the undefined values from the filtered data
   * @param {ChartContextType<LineData>["data"]} data
   * @param {ChartContextType<LineData>["meta"]} meta
   * @param {ChartContextType<LineData>["series"]} series
   * @returns {Line}
   */
  getData(
    data: ChartContextType<LineData>["values"]["data"],
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"]
  ): Line {
    return _.flatMap(_.omitBy(this.getConsolidatedData(data, meta, series), _.isUndefined)) as Line;
  }

  /**
   * Return the series to implement in subclasses
   * @param {ChartContextType<LineData>["values"]["meta"]} meta
   * @param {ChartContextType<LineData>["values"]["series"]} series
   * @param {Record<string, DataDescriptorEntity>} variants
   * @returns {SeriesType}
   */
  getSeries(
    meta: ChartContextType<LineData>["values"]["meta"],
    series: ChartContextType<LineData>["values"]["series"],
    variants: Record<string, DataDescriptorEntity>
  ): SeriesType {
    return series;
  }

  /**
   * Map predictions into the line data
   * @param {Line} data
   * @return {Line}
   */
  mapAnomalies(data: Line): Line {
    return _.map(data, ({ y, anomaly, ...rest }) => ({
      y, // y: _.get(anomaly, ["prediction"], y),
      anomaly,
      ...rest
    }));
  }

  /**
   * Format the y-axis label
   * @param {string} value
   * @param {DataPointsMeta} meta
   * @returns {string | number}
   */
  formatYLabel(value: string, meta: DataPointsMeta): string | number {
    const axisUnit = _.get(meta, ["yAxis", "unit"]);
    const isTimeOfDay = axisUnit === "timeOfDay" && _.isNumber(Number(value));
    let label: string | number = value;

    if (isTimeOfDay) {
      label = getTimeOfDay(Number(value));
    } else if (_.isNumber(Number(value))) {
      label = _.round(Number(value), 4);
    }

    return label;
  }

  /**
   * Format the tooltip
   * @param {Datum} datum
   * @param {DataPointsMeta} meta
   * @param {{roundingPrecision?: number}} options
   * @returns {{name: string, value: string}}
   */
  formatTooltip(
    datum: Datum,
    meta: DataPointsMeta,
    options: { roundingPrecision?: number } = {}
  ): { name: string; value: string } {
    const { y, seriesField } = datum;
    const series = _.get(meta, ["series"], {});
    const { roundingPrecision } = options;
    const name = _.get(series, ["keys", seriesField], seriesField);

    let value = y;

    if (_.isNumber(y)) {
      if (roundingPrecision) {
        value = y.toFixed(roundingPrecision);
      } else {
        value = _.round(y, 2);
      }
    } else if (_.isNull(y)) {
      value = "N/A";
    }

    return {
      name,
      value
    };
  }

  /**
   * Resolve the event annotations
   * @note this is basically a custom Line in the chart which will receive manipulated geometry
   * @param {Line} data
   * @param {Record<string, ExtendedEventEntity[]>} eventGroups
   * @param {Record<EventSourceType, boolean>} showingEvents
   * @returns {{x: string, y: number, seriesField: string, events: any[]}[]}
   */
  resolveEventAnnotations(
    data: Line,
    eventGroups: Record<string, ExtendedEventEntity[]>,
    showingEvents?: Record<EventSourceType, boolean>
  ): { x: string; y: number; seriesField: string; events: any[] }[] {
    /**
     * Make the data unique by x, we only care about the dates
     * @type {{x: string, y: Nullable<number>, seriesField: string, context?: PointContext, missing?: boolean, anomaly?: {prediction?: number, type?: string}}[]}
     */
    const uniqueByX = _.uniqBy(data, "x");

    /**
     * Get a list of visible event types
     * @type {string[]}
     */
    const visibleEvents: string[] = _.isPlainObject(showingEvents)
      ? _.keys(_.omitBy(showingEvents, (on) => !on))
      : [];

    return _.map(uniqueByX, ({ x }) => {
      /**
       * Grab all events of the given date
       * @type {Exclude<Record<string, ExtendedEventEntity[]>[string], undefined> | any[]}
       */
      const eventsOfX = _.get(eventGroups, [x], []);

      /**
       * Grab the events which are supposed to be visible
       * @type {any[]}
       */
      const filteredEvents = _.filter(eventsOfX, ({ source: { type } }) => visibleEvents.includes(type));

      return {
        x,
        /**
         * This trick allows to easily determine the height of the graph in pixels.
         * The value 0 will become "bottom of graph".
         */
        y: 0,
        seriesField: "farm-event",
        /**
         * Append the events to the data
         */
        events: filteredEvents
      };
    });
  }
}
